import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { AtcScreen } from "~/features/addToCart/flow/types";
import type { CloseIntent } from "~/features/addToCart/flow/useAtcFlowController";

export interface AddToCartFlowContextValue {
  currentScreen: AtcScreen;
  continueWithoutContract: () => void;
  markContractLoadErrored: () => void;
  editMode: boolean;
  requestClose: (intent?: CloseIntent) => void;
  restrictionQuery: string;
  submit: () => void;
}

interface AddToCartFlowProviderProps extends AddToCartFlowContextValue {
  children?: ReactNode;
}

const AddToCartFlowContext = createContext<AddToCartFlowContextValue | null>(null);

export const AddToCartFlowProvider = ({
  children,
  currentScreen,
  continueWithoutContract,
  markContractLoadErrored,
  requestClose,
  editMode,
  restrictionQuery,
  submit,
}: AddToCartFlowProviderProps) => {
  const value = useMemo(
    () => ({
      currentScreen,
      continueWithoutContract,
      markContractLoadErrored,
      requestClose,
      editMode,
      restrictionQuery,
      submit,
    }),
    [currentScreen, continueWithoutContract, markContractLoadErrored, requestClose, editMode, restrictionQuery, submit],
  );

  return <AddToCartFlowContext.Provider value={value}>{children}</AddToCartFlowContext.Provider>;
};

export function useAddToCartFlowContext() {
  const context = useContext(AddToCartFlowContext);

  if (!context) {
    throw new Error("useAddToCartFlowContext must be used within AddToCartFlowProvider.");
  }

  return context;
}
